import { FC, useContext, useEffect, useState, useMemo } from 'react'
import ExposeGallery from './expose-gallery'
import {
    ExposeContentWrapper,
    Sidebar,
    Content,
    ExposeMobileWrapper,
    ExposeDesktopWrapper,
    FooterWrapper,
    TitleContentWrapper,
    ComponentSeparator,
} from './styles'
import TitleBar from './title-bar'
import RatingsBar from './ratings-bar'
import StatsBar from './stats-bar'
import Description from './description'
import Amenities from './amenities'
import Location from './location'
import Contact from './contact'
import Price from './price'
import Review from './review'
import WishlistShareBar from './wishlist-share-bar'
import WhyBookThisHome from './why-book-this-home'
import SidebarComponent from './side-bar'
import { ThemeContext } from 'styled-components'
import { DatePickerAlignment } from '../common/date-picker/types'
import { GalleryLayout, ScreenContext } from 'react-components'
import CustomerObjectsSection from './customer-objects'
import useSearchBar from './hooks/use-searchbar'
import { useDynamicAvailability } from '../../hooks/availability'
import { useLivePricing } from '../../hooks/pricing'
import { PropertyDetails } from '../../models/property-details-page/property-details-model'
import { RegionNodeTransport } from '../../transports/property-details-page-transport'
import { useDateFormatter } from '../../hooks/date'
import createChildrenAges from './utils/get-children-ages'
import { useTranslation } from '../../hooks/locale'
import usePriceFormatter from '../../hooks/price'
import NavigationBar from './navigation-bar'
import NavigationSectionTransport from '../../transports/common/navigation-section-transport'
import FooterSectionTransport from '../../transports/common/footer-section-transport'
import FooterSection from '../common/footer'
import FreeCancellation from './free-cancellation'
import Footer from './footer'
import HeaderSearchbar from './header-searchbar'
import BreadcrumbSection from '../../components/landing-page/breadcrumb-section'
import LocationBar from './location-bar'
import StickyFooter from './sticky-footer'

import dynamic from 'next/dynamic'
import SkeletonAvailabilityCalendar from './availability-calendar/skeleton-calendar'
import { useLoadUnits } from '../../hooks/entity'
import { useComboLivePricing } from '../../hooks/combo-pricing'
import { encodeToBase64 } from '../../utils/encoding-utils'
import { getUnitBasedProps } from './utils/get-price-props'
import InquiryContactDetailsTransport from '../../transports/inquiry-contact-details-transport'
import LocalStorageService from '../../services/local-storage-service'
import { usePropertyDetailsContext } from '../../hooks/property-details'
import { FeatureFilterTrackingEnum, Features } from '../../models/common/features-model'
import { SuitablesModel, SuitableStates } from '../../models/common/suitables-model'
import { AB_KEY, AB_VARIANT_VERSION } from '../../common/constants'
import { useABVariant } from '../../utils/hooks'
import { trackSrlObjectClick } from '../../utils/track-utils'
import { CookieManagerService } from '../../services'
import LazyEntitySectionLoading from './entity-section/lazy-entity-section'
import { useRouter } from 'next/router'
import SimilarProperty from './similar-properties'
import GYGExperiencesSection from '../common/gyg-experiences-section'
import config from '../../common/config'
import { pageTypeEventCategories } from '../../services/analytics-service/categories'
import { BreadcrumbList } from '../../models/common/breadcrumb-model'
import { Offer } from '../../models/common/offers-model'

const LazyAvailabilityCalendar = dynamic(() => import('./availability-calendar'), {
    ssr: false,
    loading: SkeletonAvailabilityCalendar,
})

const LazyEntitySection = dynamic(() => import('./entity-section'), {
    ssr: false,
    loading: LazyEntitySectionLoading as any,
})

type Props = {
    isLoggedIn: boolean
    userDetails: any
    url: string
    pathQueryParams: string
    listingId: string
    navigationSection: NavigationSectionTransport
    footerSection: FooterSectionTransport
    regionNode: RegionNodeTransport
    propertyDetailsFormatter: PropertyDetails
}

const PropertyDetailsPage: FC<Props> = ({
    userDetails,
    isLoggedIn,
    url,
    pathQueryParams,
    listingId,
    navigationSection,
    footerSection,
    regionNode,
    propertyDetailsFormatter,
}) => {
    const { query } = useRouter()
    const theme = useContext(ThemeContext)
    const { isMobile } = useContext(ScreenContext)
    const { format } = useDateFormatter()
    const { t } = useTranslation()
    const formatPrice = usePriceFormatter()
    const { propertyDetails, isComboListing } = usePropertyDetailsContext()

    const { searchbar, updateSearchbar } = useSearchBar({
        query,
        location: '',
    })

    const features: Features = propertyDetails?.features || {}
    const petsFeature = features[FeatureFilterTrackingEnum.FEATURE_PETS]
    let petsAllowed = petsFeature?.state === SuitableStates.SUITABLE_STATUS_ALLOWED

    const {
        isLoading: isAvailabilityLoading,
        error,
        availableDates,
        departureBasedAvailableDates,
        arrivalBasedAvailableDates,
    } = useDynamicAvailability({
        searchbarData: searchbar,
        listingId: listingId,
    })

    const params = useMemo(
        () => ({
            listingId: listingId,
            arrival: searchbar.arrival ? format(searchbar.arrival, 'yyyy-MM-dd') : null,
            departure: searchbar.departure ? format(searchbar.departure, 'yyyy-MM-dd') : null,
            childrenAges: createChildrenAges(searchbar.kids).join(','),
            pets: searchbar.pets,
            adults: searchbar.adults,
            optionalServicesQuantity: '',
            selectedOptionalServices: '',
            bypassCache: true,
        }),
        [listingId, searchbar.arrival, searchbar.departure, searchbar.kids, searchbar.pets, searchbar.adults, format],
    )

    const arrival = searchbar.arrival ? format(searchbar.arrival, 'yyyy-MM-dd') : undefined
    const departure = searchbar.departure ? format(searchbar.departure, 'yyyy-MM-dd') : undefined

    const persons = searchbar.adults + searchbar.kids || undefined

    const {
        isLoading: isNormalPricingLoading,
        error: normalPricingErrorCode,
        pricingDetails: normalPricingDetails,
    } = useLivePricing(
        params,
        arrivalBasedAvailableDates,
        departureBasedAvailableDates,
        propertyDetails?.stats?.maxPersons,
        format,
        t,
        formatPrice,
        isAvailabilityLoading,
    )

    const {
        units: unitsData,
        isLoading: isUnitsLoading,
        hasMore: hasMoreUnits,
        loadedObjectIds,
        totalUnits,
        loadMoreUnits,
        onButtonClick: onSelectDatesButtonClick,
        selectDateIndicator: selectDateIndicatorForComboListing,
        selectedIndex,
        setSelectedIndex,
    } = useLoadUnits(
        {
            listingId,
            arrival: searchbar.arrival ? format(searchbar.arrival, 'yyyy-MM-dd') : null,
            departure: searchbar.departure ? format(searchbar.departure, 'yyyy-MM-dd') : null,
            adults: searchbar.adults,
            childrenAges: createChildrenAges(searchbar.kids).join(','),
            filter: encodeToBase64(pathQueryParams),
        },
        propertyDetailsFormatter,
    )

    const { comboPrices } = useComboLivePricing(
        {
            arrival: searchbar.arrival ? format(searchbar.arrival, 'yyyy-MM-dd') : null,
            departure: searchbar.departure ? format(searchbar.departure, 'yyyy-MM-dd') : null,
            childrenAges: createChildrenAges(searchbar.kids).join(','),
            pets: 0,
            adults: searchbar.adults,
        },
        loadedObjectIds,
        propertyDetails?.stats?.maxPersons,
        format,
        t,
        formatPrice,
    )

    const {
        pricingDetails,
        isPricingLoading,
        pricingErrorCode,
        bookingType,
        offers,
        showVatTax,
        isTraumPaymentEnabled,
        showCalendar,
        maxTotalGuests,
        selectedUnitName,
    } = useMemo(
        () =>
            getUnitBasedProps({
                selectedIndex,
                isComboListing,
                unitsData,
                propertyDetails,
                normalPricingDetails,
                isNormalPricingLoading,
                normalPricingErrorCode,
                comboPrices,
                t,
            }),
        [
            selectedIndex,
            isComboListing,
            unitsData,
            propertyDetails,
            normalPricingDetails,
            isNormalPricingLoading,
            normalPricingErrorCode,
            comboPrices,
            t,
        ],
    )

    const { isProvidedVariantActive } = useABVariant(AB_KEY.CHECKOUT_MERGER, AB_VARIANT_VERSION.ONE)
    const { isProvidedVariantActive: isPdpRecommendationObjectsActive } = useABVariant(
        AB_KEY.PDP_RECOMMENDATION,
        AB_VARIANT_VERSION.ONE,
    )

    const { isProvidedVariantActive: isGYGEnabled } = useABVariant(AB_KEY.EXPERIENCES_SECTION, AB_VARIANT_VERSION.ONE)

    if (!isProvidedVariantActive) {
        petsAllowed = false
    }

    const [inquiryContactDetails, setInquiryContactDetails] = useState<InquiryContactDetailsTransport | null>(null)

    useEffect(() => {
        LocalStorageService.getItem<InquiryContactDetailsTransport>(LocalStorageService.FASTLANE_PERSON_DETAILS).then(
            details => {
                setInquiryContactDetails(details)
            },
        )
    }, [])

    useEffect(() => {
        const cookieKey = `srlObjectClicked_${listingId}`
        const cookieExists = CookieManagerService.getCookie(cookieKey)

        if (
            cookieExists &&
            !isPricingLoading &&
            !isAvailabilityLoading &&
            arrivalBasedAvailableDates !== null &&
            (pricingErrorCode !== null || pricingDetails !== null || !query?.arrival || !query?.departure)
        ) {
            const searchRequestId = typeof query?.searchRequestId === 'string' ? query.searchRequestId : undefined
            trackSrlObjectClick({
                listingId,
                searchbar,
                pricingDetails,
                pricingErrorCode,
                t,
                format,
                isComboListing,
                propertyTitle: propertyDetails?.title,
                propertyFullTitle: propertyDetails?.fullTitle,
                providerId: propertyDetails?.general?.providerId,
                searchRequestId,
            })
        }
    }, [
        isPricingLoading,
        isAvailabilityLoading,
        arrivalBasedAvailableDates !== null,
        pricingDetails !== null,
        pricingErrorCode,
        query?.arrival,
        query?.departure,
        listingId,
        isComboListing,
    ])

    const breadcrumbList = useMemo(() => new BreadcrumbList(t), [t])
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])

    if (!propertyDetails) {
        return null
    }

    return (
        <>
            <HeaderSearchbar
                isLoggedIn={isLoggedIn}
                userDetails={userDetails}
                searchbar={searchbar}
                url={url}
                navigationSection={navigationSection}
                listingId={listingId}
                regionNode={regionNode}
                petsAllowed={false}
                {...(!isMobile && { updateSearchbar: updateSearchbar })}
            />

            {!isMobile && <ComponentSeparator header />}
            <NavigationBar listingId={propertyDetails.id} />
            {isMobile && <WishlistShareBar listingId={propertyDetails.id} />}

            <ExposeMobileWrapper>
                <ExposeGallery
                    showThumbnail={false}
                    layout={GalleryLayout.CAROUSEL}
                    images={propertyDetails.gallery}
                    video={propertyDetails?.video}
                    bookingType={bookingType}
                    searchbar={searchbar}
                    propertyName={propertyDetails.fullTitle}
                    onSelectDatesButtonClick={onSelectDatesButtonClick}
                    pricingDetails={pricingDetails}
                    inquiryContactDetails={inquiryContactDetails}
                    pricingErrorCode={pricingErrorCode}
                    isPricingLoading={isPricingLoading}
                    isAvailabilityLoading={isAvailabilityLoading}
                    updateSearchbar={updateSearchbar}
                    availableDates={availableDates}
                    userDetails={userDetails}
                    petsAllowed={petsAllowed}
                />
            </ExposeMobileWrapper>

            {!isMobile && (
                <ExposeDesktopWrapper>
                    <ExposeGallery
                        searchbar={searchbar}
                        propertyName={propertyDetails.fullTitle}
                        bookingType={bookingType}
                        images={propertyDetails.gallery}
                        video={propertyDetails?.video}
                        showThumbnail
                        layout={GalleryLayout.GRID}
                        imageBorderRadius={`${theme?.typography.border.radius.medium}`}
                        onSelectDatesButtonClick={onSelectDatesButtonClick}
                        pricingDetails={pricingDetails}
                        inquiryContactDetails={inquiryContactDetails}
                        pricingErrorCode={pricingErrorCode}
                        isPricingLoading={isPricingLoading}
                        isAvailabilityLoading={isAvailabilityLoading}
                        updateSearchbar={updateSearchbar}
                        availableDates={availableDates}
                        userDetails={userDetails}
                        petsAllowed={petsAllowed}
                    />
                </ExposeDesktopWrapper>
            )}
            <ExposeContentWrapper>
                <Content>
                    {!!propertyDetails?.breadcrumbs && propertyDetails?.breadcrumbs.length > 0 && (
                        <BreadcrumbSection
                            breadcrumbs={propertyDetails.breadcrumbs}
                            url={url}
                            containerPadding={'1rem 0 0'}
                            hideMobile
                        />
                    )}
                    {isMobile ? (
                        // OLD: Structure for mobile
                        <TitleBar title={propertyDetails.fullTitle} />
                    ) : (
                        // NEW: Structure for desktop
                        <TitleContentWrapper>
                            <TitleBar title={propertyDetails.fullTitle} />
                            <WishlistShareBar listingId={propertyDetails.id} />
                        </TitleContentWrapper>
                    )}
                    <RatingsBar
                        ratingCount={propertyDetails.reviews.ratingsCount}
                        averageRating={propertyDetails.reviews.badge.avg}
                        trust={propertyDetails.trust}
                    />
                    <StatsBar stats={propertyDetails.stats} />
                    <ExposeMobileWrapper>
                        <LocationBar showMapHyperlink region={propertyDetails.propertyRegion} />
                    </ExposeMobileWrapper>
                    <ExposeMobileWrapper>
                        <Price
                            layout="stretched"
                            datePickerAlignment={DatePickerAlignment.LEFT}
                            showPriceTitle={false}
                            hideExtraInfo
                            notes={propertyDetails.notes}
                            bookingCancellationPolicy={propertyDetails.bookingCancellationPolicy}
                            updateSearchbar={updateSearchbar}
                            searchbar={searchbar}
                            availableDates={availableDates}
                            isAvailabilityLoading={isAvailabilityLoading}
                            pricingDetails={pricingDetails}
                            isPricingLoading={isPricingLoading}
                            pricingErrorCode={pricingErrorCode}
                            bookingType={bookingType}
                            offers={offers}
                            showVatTax={showVatTax}
                            isTraumPaymentEnabled={isTraumPaymentEnabled}
                            showCalendar={showCalendar}
                            maxTotalGuests={maxTotalGuests}
                            inquiryContactDetails={inquiryContactDetails}
                            userDetails={userDetails}
                            petsAllowed={petsAllowed}
                            selectedUnitName={selectedUnitName}
                            selectDateIndicatorForComboListing={selectDateIndicatorForComboListing}
                            showPriceWidget
                            responseTimeAverage={propertyDetails.general.responseTimeAverage}
                        />
                    </ExposeMobileWrapper>
                    {!isMobile && (
                        <FreeCancellation
                            freeCancellationDays={propertyDetails.general.freeCancellationInDays}
                            isFreeCancellationAvailable={propertyDetails.general.activatedFreeCancellation}
                        />
                    )}
                    <Description description={propertyDetails.description} />
                    <WhyBookThisHome whyBookThisHomeObjects={propertyDetails.description.whyBookThisHome} />
                    <Amenities
                        features={propertyDetails.features}
                        rooms={propertyDetails.rooms}
                        extendedFeatures={propertyDetails.extendedFeatures}
                    />
                    {isComboListing && (
                        <LazyEntitySection
                            data={unitsData}
                            totalUnits={totalUnits}
                            searchbar={searchbar}
                            loadMoreUnits={loadMoreUnits}
                            onSelectDatesButtonClick={onSelectDatesButtonClick}
                            isUnitsLoading={isUnitsLoading}
                            hasMoreUnits={hasMoreUnits}
                            selectedIndex={selectedIndex}
                            setSelectedIndex={setSelectedIndex}
                            pricingDetails={comboPrices}
                        />
                    )}
                    <Price
                        layout={isMobile ? 'stretched' : 'compact'}
                        datePickerAlignment={DatePickerAlignment.LEFT}
                        showPriceTitle
                        notes={propertyDetails.notes}
                        bookingCancellationPolicy={propertyDetails.bookingCancellationPolicy}
                        updateSearchbar={updateSearchbar}
                        searchbar={searchbar}
                        availableDates={availableDates}
                        isAvailabilityLoading={isAvailabilityLoading}
                        pricingDetails={pricingDetails}
                        isPricingLoading={isPricingLoading}
                        pricingErrorCode={pricingErrorCode}
                        bookingType={bookingType}
                        offers={offers}
                        showVatTax={showVatTax}
                        isTraumPaymentEnabled={isTraumPaymentEnabled}
                        showCalendar={showCalendar}
                        maxTotalGuests={maxTotalGuests}
                        inquiryContactDetails={inquiryContactDetails}
                        userDetails={userDetails}
                        petsAllowed={petsAllowed}
                        selectedUnitName={selectedUnitName}
                        selectDateIndicatorForComboListing={selectDateIndicatorForComboListing}
                        showPriceWidget={!isMobile}
                    />
                    {isMobile && (
                        <FreeCancellation
                            freeCancellationDays={propertyDetails.general.freeCancellationInDays}
                            isFreeCancellationAvailable={propertyDetails.general.activatedFreeCancellation}
                        />
                    )}
                    <Location
                        surroundings={propertyDetails.surroundings}
                        location={propertyDetails.location}
                        activities={propertyDetails.activities}
                        leisure={propertyDetails.description.leisure}
                        surroundingLocation={propertyDetails.description.surroundings}
                        journey={propertyDetails.description.journey}
                        regionLabel={propertyDetails.description.regionLabel}
                        region={propertyDetails.propertyRegion}
                        hideExactPosition={propertyDetails.hideExactPosition}
                    />
                    {isGYGEnabled && !!config.gygPartnerId && !!regionNode.title && (
                        <>
                            <GYGExperiencesSection
                                destinationName={regionNode.title}
                                partnerId={config.gygPartnerId}
                                campaign="property-details"
                                compact
                                showNewTag
                            />
                            <ComponentSeparator />
                        </>
                    )}
                    {!isComboListing && (
                        <LazyAvailabilityCalendar
                            showCalendar={propertyDetails.general.showAvailabilityCalendar}
                            listingId={listingId}
                        />
                    )}
                    <Contact
                        contact={propertyDetails.contact}
                        bookingType={bookingType}
                        providerId={propertyDetails.general.providerId}
                        searchbar={searchbar}
                        pricingDetails={pricingDetails}
                        inquiryContactDetails={inquiryContactDetails}
                        pricingErrorCode={pricingErrorCode}
                        isPricingLoading={isPricingLoading}
                        isAvailabilityLoading={isAvailabilityLoading}
                        updateSearchbar={updateSearchbar}
                        availableDates={availableDates}
                        userDetails={userDetails}
                        petsAllowed={petsAllowed}
                    />
                    <Review ratings={propertyDetails.reviews} propertyTypeConfig={propertyDetails.propertyTypeConfig} />
                    <CustomerObjectsSection
                        customerObjects={propertyDetails.customerObjects}
                        contact={propertyDetails.contact}
                        bookingType={bookingType}
                    />
                    {isPdpRecommendationObjectsActive && (
                        <SimilarProperty
                            arrival={arrival}
                            departure={departure}
                            persons={persons}
                            listingId={Number(listingId)}
                            regionId={regionNode.id}
                            filter={pathQueryParams}
                            url={url}
                            pageType={pageTypeEventCategories.PROPERTY_DETAILS_PAGE}
                            breadcrumbModel={breadcrumbList}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                        />
                    )}
                    <Footer footer={propertyDetails.footer} />
                </Content>
                <Sidebar>
                    <SidebarComponent
                        trust={propertyDetails.trust}
                        contact={propertyDetails.contact}
                        updateSearchbar={updateSearchbar}
                        searchbar={searchbar}
                        availableDates={availableDates}
                        isAvailabilityLoading={isAvailabilityLoading}
                        errorFetchingAvailability={error}
                        selectDateIndicatorForComboListing={selectDateIndicatorForComboListing}
                        pricingDetails={pricingDetails}
                        isPricingLoading={isPricingLoading}
                        pricingErrorCode={pricingErrorCode}
                        bookingType={bookingType}
                        offers={offers}
                        showVatTax={showVatTax}
                        providerId={propertyDetails.general.providerId}
                        responseTimeAverage={propertyDetails.general.responseTimeAverage}
                        isTraumPaymentEnabled={isTraumPaymentEnabled}
                        showCalendar={showCalendar}
                        maxTotalGuests={maxTotalGuests} // THIS SHOULD BE CHANGED
                        selectedUnitName={selectedUnitName}
                        inquiryContactDetails={inquiryContactDetails}
                        userDetails={userDetails}
                        petsAllowed={petsAllowed}
                    />
                </Sidebar>
            </ExposeContentWrapper>
            <FooterWrapper>
                <FooterSection
                    footerSection={footerSection}
                    footerLogo={navigationSection.navigation.logo}
                    socialMediaLinks={navigationSection.socialMediaLinks}
                />
                <StickyFooter
                    bookingType={bookingType}
                    searchbar={searchbar}
                    pricingDetails={pricingDetails}
                    onSelectDatesButtonClick={onSelectDatesButtonClick}
                    inquiryContactDetails={inquiryContactDetails}
                    pricingErrorCode={pricingErrorCode}
                    isPricingLoading={isPricingLoading}
                    isAvailabilityLoading={isAvailabilityLoading}
                    updateSearchbar={updateSearchbar}
                    availableDates={availableDates}
                    userDetails={userDetails}
                    petsAllowed={petsAllowed}
                />
            </FooterWrapper>
        </>
    )
}

export default PropertyDetailsPage
